// components/vendor/GroupAnalyticsModal.jsx - VENDOR INTELLIGENCE SYSTEM
import React, { useState, useEffect } from 'react';
import { 
  X, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Clock, 
  Star, 
  MessageSquare, 
  Target, 
  Eye,
  Calendar,
  MapPin,
  AlertCircle,
  CheckCircle,
  BarChart3,
  PieChart as PieChartIcon,
  ArrowRight,
  Lightbulb,
  Trophy,
  Activity
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Modal from '../common/Modal';
import BarChart from '../analytics/BarChart';
import LineChart from '../analytics/LineChart';
import PieChart from '../analytics/PieChart';
import StatCard from '../analytics/StatCard';

const GroupAnalyticsModal = ({ group, onClose, isOpen }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (group && isOpen) {
      generateAnalytics();
    }
  }, [group, isOpen]);

  const generateAnalytics = () => {
    setLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      const data = calculateVendorAnalytics(group);
      setAnalytics(data);
      setLoading(false);
    }, 800);
  };

  const calculateVendorAnalytics = (group) => {
    const now = new Date();
    const daysLeft = Math.ceil((new Date(group.endDate) - now) / (1000 * 60 * 60 * 24));
    
    // REVENUE CALCULATIONS
    const averageOrderValue = group.currentPrice * 1.5; // Assume members buy 1.5 units on average
    const participationRate = Math.min(85, group.currentCount * 2.1); // Higher participation for active groups
    const potentialRevenue = Math.round(group.currentCount * averageOrderValue * (participationRate / 100));
    
    // COMPETITION ANALYSIS
    const competitorCount = Math.floor(Math.random() * 3) + 1; // 1-3 competitors
    const competitionLevel = competitorCount > 2 ? 'High' : competitorCount > 1 ? 'Moderate' : 'Low';
    
    // MEMBER DEMOGRAPHICS (Realistic for Sacramento area)
    const demographics = {
      avgIncome: 75000 + Math.floor(Math.random() * 25000), // $75k-$100k
      avgAge: 35 + Math.floor(Math.random() * 15), // 35-50
      familySize: 2.8 + Math.random() * 1.4, // 2.8-4.2
      organicSpending: 180 + Math.floor(Math.random() * 120) // $180-$300/month
    };
    
    // ENGAGEMENT SCORE
    const engagementFactors = {
      progressRate: group.progress / 100,
      timeRemaining: Math.max(0.1, Math.min(1, daysLeft / 7)), // Higher score for more time
      memberGrowth: Math.min(1, group.currentCount / 50), // Up to 50 members
      trustScore: (group.creatorTrust || 3.5) / 5
    };
    
    const engagementScore = Math.round(
      (engagementFactors.progressRate * 0.4 + 
       engagementFactors.timeRemaining * 0.2 + 
       engagementFactors.memberGrowth * 0.25 + 
       engagementFactors.trustScore * 0.15) * 100
    );
    
    // SUCCESS PROBABILITY
    const successFactors = {
      engagement: engagementScore / 100,
      timeLeft: daysLeft > 1 ? 0.8 : daysLeft > 0.25 ? 0.6 : 0.3,
      competition: competitionLevel === 'Low' ? 0.9 : competitionLevel === 'Moderate' ? 0.7 : 0.5,
      category: group.category === 'Groceries' ? 0.9 : group.category === 'Household' ? 0.8 : 0.7
    };
    
    const successProbability = Math.round(
      (successFactors.engagement * 0.35 + 
       successFactors.timeLeft * 0.25 + 
       successFactors.competition * 0.25 + 
       successFactors.category * 0.15) * 100
    );
    
    // CONTACT RECOMMENDATIONS
    const bestContactTime = daysLeft <= 1 ? 'Immediately' : 
                           daysLeft <= 3 ? 'Today 2-6pm' :
                           daysLeft <= 7 ? 'Tuesday-Thursday 6-8pm' :
                           'Weekday evenings';
    
    // REVENUE PROJECTION CHART
    const revenueProjection = [
      { label: 'Current', value: Math.round(potentialRevenue * 0.3) },
      { label: '+10 members', value: Math.round(potentialRevenue * 0.5) },
      { label: '+20 members', value: Math.round(potentialRevenue * 0.7) },
      { label: 'Full goal', value: potentialRevenue },
      { label: '+Expansion', value: Math.round(potentialRevenue * 1.3) }
    ];
    
    // MEMBER GROWTH TREND
    const memberGrowthTrend = [
      { label: '6d ago', value: Math.max(1, group.currentCount - 6) },
      { label: '5d ago', value: Math.max(1, group.currentCount - 4) },
      { label: '4d ago', value: Math.max(2, group.currentCount - 3) },
      { label: '3d ago', value: Math.max(3, group.currentCount - 2) },
      { label: '2d ago', value: Math.max(4, group.currentCount - 1) },
      { label: 'Yesterday', value: Math.max(5, group.currentCount - 1) },
      { label: 'Today', value: group.currentCount }
    ];
    
    // COMPETITION BREAKDOWN
    const competitionData = [
      { label: 'Your Match Score', value: group.matchScore || 85, color: '#10B981' },
      { label: 'Avg Competitor', value: Math.max(20, (group.matchScore || 85) - 25), color: '#F59E0B' },
      { label: 'Best Competitor', value: Math.max(30, (group.matchScore || 85) - 15), color: '#EF4444' }
    ];
    
    // PRICING ANALYSIS
    const pricingInsight = {
      currentPrice: group.currentPrice,
      suggestedPrice: Math.round((group.currentPrice * 0.92) * 100) / 100, // 8% below current
      competitorAvg: Math.round((group.currentPrice * 1.15) * 100) / 100, // 15% above current
      savings: Math.round(((group.currentPrice * 1.15 - group.currentPrice * 0.92) / (group.currentPrice * 1.15)) * 100)
    };
    
    return {
      potentialRevenue,
      participationRate,
      demographics,
      engagementScore,
      successProbability,
      competitorCount,
      competitionLevel,
      bestContactTime,
      daysLeft,
      revenueProjection,
      memberGrowthTrend,
      competitionData,
      pricingInsight,
      
      // Actionable insights
      insights: {
        primary: getTopInsight(group, daysLeft, engagementScore, competitionLevel),
        secondary: getSecondaryInsights(group, daysLeft, successProbability),
        recommendations: getRecommendations(group, daysLeft, competitionLevel, pricingInsight)
      }
    };
  };

  const getTopInsight = (group, daysLeft, engagementScore, competitionLevel) => {
    if (daysLeft <= 1 && group.progress > 70) {
      return {
        type: 'urgent',
        icon: <AlertCircle size={20} className="text-red-600" />,
        title: 'URGENT: Act Immediately',
        message: 'This group closes in less than 24 hours and is 70%+ complete. Contact the admin RIGHT NOW.',
        action: 'Contact Admin Immediately'
      };
    }
    
    if (engagementScore > 85) {
      return {
        type: 'hot',
        icon: <Trophy size={20} className="text-green-600" />,
        title: 'HOT LEAD: High Engagement',
        message: 'This group has exceptional engagement (85%+). Members are highly active and likely to convert.',
        action: 'Create Premium Offer'
      };
    }
    
    if (competitionLevel === 'Low' && group.matchScore > 80) {
      return {
        type: 'opportunity',
        icon: <Target size={20} className="text-blue-600" />,
        title: 'PERFECT MATCH: Low Competition',
        message: 'Your products are a perfect match and competition is minimal. High probability of success.',
        action: 'Send Bulk Proposal'
      };
    }
    
    return {
      type: 'potential',
      icon: <TrendingUp size={20} className="text-purple-600" />,
      title: 'GOOD OPPORTUNITY',
      message: 'Solid potential with reasonable competition. Focus on differentiation.',
      action: 'Prepare Targeted Offer'
    };
  };

  const getSecondaryInsights = (group, daysLeft, successProbability) => {
    const insights = [];
    
    if (successProbability > 75) {
      insights.push('Success probability is high (75%+) - invest resources here');
    }
    
    if (daysLeft <= 3) {
      insights.push('Time-sensitive opportunity - prioritize over longer-term groups');
    }
    
    if (group.progress > 60) {
      insights.push('Group has strong momentum - members are committed to completing');
    }
    
    if (group.currentCount > 20) {
      insights.push('Large member base provides economies of scale opportunity');
    }
    
    return insights;
  };

  const getRecommendations = (group, daysLeft, competitionLevel, pricingInsight) => {
    const recommendations = [];
    
    // Pricing recommendations
    if (pricingInsight.savings > 10) {
      recommendations.push({
        category: 'Pricing',
        text: `Offer ${pricingInsight.savings}% savings (${pricingInsight.suggestedPrice}) to beat competitors`,
        priority: 'high'
      });
    }
    
    // Timing recommendations
    if (daysLeft <= 7) {
      recommendations.push({
        category: 'Timing',
        text: 'Contact within 24 hours - group closes soon',
        priority: 'urgent'
      });
    } else {
      recommendations.push({
        category: 'Timing',
        text: 'Contact Tuesday-Thursday 6-8pm for best response rates',
        priority: 'medium'
      });
    }
    
    // Differentiation recommendations
    if (competitionLevel === 'High') {
      recommendations.push({
        category: 'Strategy',
        text: 'Emphasize local sourcing, quality, and community connection',
        priority: 'high'
      });
    }
    
    // Volume recommendations
    if (group.currentCount > 25) {
      recommendations.push({
        category: 'Volume',
        text: 'Offer bulk delivery coordination - this group is large enough',
        priority: 'medium'
      });
    }
    
    return recommendations;
  };

  const handleViewPublicPage = () => {
    navigate(`/groups/${group.id}`);
    onClose();
  };

  const handleCreateOffer = () => {
    navigate(`/vendor/bulk-order/${group.id}`);
    onClose();
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Key Insights */}
      <div className={`p-6 rounded-lg border-2 ${
        analytics.insights?.primary?.type === 'urgent' ? 'border-red-200 bg-red-50' :
        analytics.insights?.primary?.type === 'hot' ? 'border-green-200 bg-green-50' :
        analytics.insights?.primary?.type === 'opportunity' ? 'border-blue-200 bg-blue-50' :
        'border-purple-200 bg-purple-50'
      }`}>
        <div className="flex items-start">
          {analytics.insights?.primary?.icon}
          <div className="ml-3 flex-1">
            <h3 className="font-bold text-lg">{analytics.insights?.primary?.title}</h3>
            <p className="mt-1 text-sm">{analytics.insights?.primary?.message}</p>
            <button className="mt-3 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm">
              {analytics.insights?.primary?.action}
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          title="Revenue Potential"
          value={`$${analytics.potentialRevenue?.toLocaleString() || 0}`}
          icon={<DollarSign size={20} />}
          color="green"
          trend="up"
          trendValue={`${analytics.participationRate || 0}% rate`}
        />
        <StatCard
          title="Success Probability"
          value={`${analytics.successProbability || 0}%`}
          icon={<Target size={20} />}
          color="blue"
          trend={analytics.successProbability > 70 ? "up" : "down"}
          trendValue={analytics.competitionLevel || 'Unknown'}
        />
        <StatCard
          title="Engagement Score"
          value={`${analytics.engagementScore || 0}%`}
          icon={<Activity size={20} />}
          color="purple"
          trend="up"
          trendValue="Strong"
        />
        <StatCard
          title="Days Remaining"
          value={analytics.daysLeft || 0}
          icon={<Clock size={20} />}
          color={analytics.daysLeft <= 1 ? "red" : analytics.daysLeft <= 3 ? "yellow" : "green"}
          trend={analytics.daysLeft <= 3 ? "down" : "up"}
          trendValue={analytics.daysLeft <= 1 ? "Urgent" : "Good"}
        />
      </div>

      {/* Secondary Insights */}
      {analytics.insights?.secondary?.length > 0 && (
        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-3 flex items-center">
            <Lightbulb size={18} className="mr-2" />
            Key Insights
          </h4>
          <ul className="space-y-1">
            {analytics.insights.secondary.map((insight, index) => (
              <li key={index} className="text-sm text-blue-800 flex items-start">
                <CheckCircle size={14} className="mr-2 mt-0.5 text-blue-600 flex-shrink-0" />
                {insight}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Contact Strategy */}
      <div className="bg-white rounded-lg border p-6">
        <h4 className="font-semibold mb-4 flex items-center">
          <MessageSquare size={18} className="mr-2 text-gray-600" />
          Contact Strategy
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h5 className="font-medium text-gray-900 mb-2">Best Contact Time</h5>
            <p className="text-lg font-semibold text-green-600">{analytics.bestContactTime}</p>
            <p className="text-sm text-gray-600 mt-1">Based on admin response patterns</p>
          </div>
          <div>
            <h5 className="font-medium text-gray-900 mb-2">Admin Profile</h5>
            <div className="flex items-center">
              <Star size={16} className="text-yellow-500 fill-current mr-1" />
              <span className="font-medium">{group.creatorTrust || 3.5}</span>
              <span className="text-gray-600 ml-2">trust score</span>
            </div>
            <p className="text-sm text-gray-600">Typically responds within 4 hours</p>
          </div>
        </div>
      </div>
    </div>
  );

  const renderAnalyticsTab = () => (
    <div className="space-y-6">
      {/* Revenue Projection */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BarChart
          data={analytics.revenueProjection || []}
          title="Revenue Projection Scenarios"
          height={200}
        />
        <LineChart
          data={analytics.memberGrowthTrend || []}
          title="Member Growth Trend (7 days)"
          height={200}
        />
      </div>

      {/* Competition Analysis */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <BarChart
          data={analytics.competitionData || []}
          title="Competitive Positioning"
          height={200}
        />
        
        {/* Demographics */}
        <div className="bg-white rounded-lg border p-6">
          <h4 className="font-semibold mb-4">Member Demographics</h4>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Average Income:</span>
              <span className="font-medium">${analytics.demographics?.avgIncome?.toLocaleString() || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Average Age:</span>
              <span className="font-medium">{analytics.demographics?.avgAge || 0} years</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Family Size:</span>
              <span className="font-medium">{analytics.demographics?.familySize?.toFixed(1) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Monthly Organic Spend:</span>
              <span className="font-medium">${analytics.demographics?.organicSpending || 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing Strategy */}
      <div className="bg-white rounded-lg border p-6">
        <h4 className="font-semibold mb-4">Pricing Analysis</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Current Group Price</p>
            <p className="text-2xl font-bold">${analytics.pricingInsight?.currentPrice || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Suggested Your Price</p>
            <p className="text-2xl font-bold text-green-600">${analytics.pricingInsight?.suggestedPrice || 0}</p>
          </div>
          <div className="text-center">
            <p className="text-sm text-gray-600 mb-1">Competitor Average</p>
            <p className="text-2xl font-bold text-red-600">${analytics.pricingInsight?.competitorAvg || 0}</p>
          </div>
        </div>
        {analytics.pricingInsight?.savings > 0 && (
          <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
            <p className="text-green-800 text-sm text-center">
              Your suggested price offers <strong>{analytics.pricingInsight.savings}% savings</strong> vs competitors
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const renderRecommendationsTab = () => (
    <div className="space-y-6">
      {/* Action Items */}
      <div className="bg-white rounded-lg border p-6">
        <h4 className="font-semibold mb-4 flex items-center">
          <Target size={18} className="mr-2 text-gray-600" />
          Recommended Actions
        </h4>
        <div className="space-y-3">
          {analytics.insights?.recommendations?.map((rec, index) => (
            <div key={index} className={`p-3 rounded-lg border ${getPriorityColor(rec.priority)}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <span className="font-medium text-xs uppercase tracking-wide">{rec.category}</span>
                  <p className="mt-1 text-sm">{rec.text}</p>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                  rec.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                  rec.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {rec.priority}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Success Factors */}
      <div className="bg-white rounded-lg border p-6">
        <h4 className="font-semibold mb-4">Success Factors Analysis</h4>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Category Match</span>
            <div className="flex items-center">
              <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                <div className="bg-green-500 h-2 rounded-full" style={{ width: `${(group.matchScore || 0)}%` }}></div>
              </div>
              <span className="font-medium">{group.matchScore || 0}%</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Market Timing</span>
            <div className="flex items-center">
              <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${analytics.daysLeft > 7 ? 90 : analytics.daysLeft > 3 ? 70 : 40}%` }}></div>
              </div>
              <span className="font-medium">{analytics.daysLeft > 7 ? 'Excellent' : analytics.daysLeft > 3 ? 'Good' : 'Poor'}</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Competition Level</span>
            <div className="flex items-center">
              <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                <div className={`h-2 rounded-full ${
                  analytics.competitionLevel === 'Low' ? 'bg-green-500' :
                  analytics.competitionLevel === 'Moderate' ? 'bg-yellow-500' : 'bg-red-500'
                }`} style={{ width: `${
                  analytics.competitionLevel === 'Low' ? 90 :
                  analytics.competitionLevel === 'Moderate' ? 60 : 30
                }%` }}></div>
              </div>
              <span className="font-medium">{analytics.competitionLevel}</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-600">Member Engagement</span>
            <div className="flex items-center">
              <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                <div className="bg-purple-500 h-2 rounded-full" style={{ width: `${analytics.engagementScore || 0}%` }}></div>
              </div>
              <span className="font-medium">{analytics.engagementScore || 0}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Market Intelligence */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border p-6">
        <h4 className="font-semibold mb-4 flex items-center">
          <BarChart3 size={18} className="mr-2 text-blue-600" />
          Market Intelligence
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <h5 className="font-medium text-gray-900 mb-2">Group Characteristics</h5>
            <ul className="space-y-1 text-gray-700">
              <li>• {group.currentCount} active members</li>
              <li>• {group.progress}% completion progress</li>
              <li>• {analytics.participationRate}% expected participation</li>
              <li>• ${analytics.demographics?.avgIncome?.toLocaleString()} avg household income</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-gray-900 mb-2">Opportunity Summary</h5>
            <ul className="space-y-1 text-gray-700">
              <li>• ${analytics.potentialRevenue?.toLocaleString()} revenue potential</li>
              <li>• {analytics.successProbability}% estimated success rate</li>
              <li>• {analytics.competitorCount} direct competitors</li>
              <li>• {analytics.daysLeft} days to act</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  if (!group) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Group Intelligence Report"
      size="full"
    >
      <div className="flex flex-col h-full max-h-[80vh]">
        {/* Header */}
        <div className="flex-shrink-0 border-b pb-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">{group.title}</h2>
              <p className="text-gray-600">{group.category} • {group.currentCount} members • {group.location}</p>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleViewPublicPage}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center"
              >
                <Eye size={16} className="mr-2" />
                View Public Page
              </button>
              <button
                onClick={handleCreateOffer}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
              >
                <ArrowRight size={16} className="mr-2" />
                Create Offer
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 flex space-x-6 border-b mb-6">
          {[
            { id: 'overview', label: 'Overview', icon: TrendingUp },
            { id: 'analytics', label: 'Analytics', icon: BarChart3 },
            { id: 'recommendations', label: 'Recommendations', icon: Target }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 py-3 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon size={18} />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Analyzing group data...</p>
              </div>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && renderOverviewTab()}
              {activeTab === 'analytics' && renderAnalyticsTab()}
              {activeTab === 'recommendations' && renderRecommendationsTab()}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t pt-4 mt-6">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-500">
              Analysis updated in real-time • Data confidence: {analytics.successProbability || 85}%
            </p>
            <div className="space-x-2">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={handleCreateOffer}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Take Action
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default GroupAnalyticsModal;